import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/** The translator as react-i18next hands it over, so a helper can take it without widening its type. */
type Translate = ReturnType<typeof useTranslation>["t"];
import { formatDate } from "../../lib/format";
import { complaintsApi, type ComplaintStaffOption, type ComplaintTeamMember } from "../../api/complaints";
import type { ApiError } from "../../api/client";
import { useToast } from "../Toast";
import { Select } from "../Select";
import Ic from "../icons.jsx";

/**
 * The team on one complaint. KK54: it is formed „u zavisnosti od termina koji je osnov reklamacije", so
 * it is named per complaint rather than drawn from a standing list.
 *
 * <p>People who have left stay on the page, greyed out and dated: who worked on a complaint is part of
 * the record and reading it back is the point. The quality service has sixteen people, so the person
 * named is the one who gets the notification, not everybody.
 */
export function ComplaintTeamPanel({
  complaintId,
  canManage,
}: {
  complaintId: string;
  canManage: boolean;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [members, setMembers] = useState<ComplaintTeamMember[] | null>(null);
  const [staff, setStaff] = useState<ComplaintStaffOption[]>([]);
  const [userId, setUserId] = useState("");
  const [roleInTeam, setRoleInTeam] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      setMembers(await complaintsApi.listTeam(complaintId));
    } catch {
      // Best effort: the rest of the complaint still reads.
      setMembers([]);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the loader is stable per complaint
  }, [complaintId]);

  useEffect(() => {
    if (!canManage) return;
    complaintsApi.staff().then(setStaff).catch(() => setStaff([]));
  }, [canManage]);

  const active = (members ?? []).filter((m) => m.removedAt === null);
  const past = (members ?? []).filter((m) => m.removedAt !== null);
  const onTeam = new Set(active.map((m) => m.userId));

  async function add() {
    if (!userId) return;
    setBusy(true);
    try {
      await complaintsApi.addTeamMember(complaintId, { userId, roleInTeam: roleInTeam.trim() || null });
      setUserId("");
      setRoleInTeam("");
      await load();
    } catch (e) {
      toast.error(errorText(t, e as ApiError));
    } finally {
      setBusy(false);
    }
  }

  async function remove(memberId: string) {
    setBusy(true);
    try {
      await complaintsApi.removeTeamMember(complaintId, memberId);
      await load();
    } catch (e) {
      toast.error(errorText(t, e as ApiError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <div className="card-h">
        <h2>{t("complaints.team.title", "Tim")}</h2>
        <span className="muted">{t("complaints.team.count", "{{n}} u timu", { n: active.length })}</span>
      </div>
      <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {active.length === 0 && past.length === 0 ? (
          <div className="empty">{t("complaints.team.empty", "Tim još nije određen.")}</div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>{t("complaints.team.person", "Osoba")}</th>
                <th>{t("complaints.team.role", "Zadatak u timu")}</th>
                <th>{t("complaints.team.assigned", "Određen")}</th>
                {canManage && <th aria-label={t("complaints.team.actions", "Radnje")} />}
              </tr>
            </thead>
            <tbody>
              {[...active, ...past].map((m) => (
                <tr key={m.id} style={m.removedAt ? { opacity: 0.55 } : undefined}>
                  <td data-label={t("complaints.team.person", "Osoba")}>
                    {m.userName ?? <code>{m.userId.slice(0, 8)}</code>}
                    {m.removedAt && (
                      <>
                        {" "}
                        <span className="tag neutral">{t("complaints.team.left", "više nije u timu")}</span>
                      </>
                    )}
                  </td>
                  <td data-label={t("complaints.team.role", "Zadatak u timu")}>
                    {m.roleInTeam ?? <span className="muted">—</span>}
                  </td>
                  <td data-label={t("complaints.team.assigned", "Određen")}>
                    <span className="muted">
                      {formatDate(m.assignedAt)}
                      {m.assignedByName ? ` · ${m.assignedByName}` : ""}
                    </span>
                  </td>
                  {canManage && (
                    <td style={{ textAlign: "right" }}>
                      {m.removedAt === null && (
                        <button type="button" className="btn sm ghost" disabled={busy} onClick={() => void remove(m.id)}>
                          {t("complaints.team.remove", "Skloni")}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {canManage && (
          <div className="toolbar">
            <Select<string>
              value={userId}
              onChange={setUserId}
              ariaLabel={t("complaints.team.pick", "Dodaj u tim")}
              style={{ minWidth: 220 }}
              options={[
                { value: "", label: t("complaints.team.pick", "Dodaj u tim") },
                ...staff.filter((s) => !onTeam.has(s.id)).map((s) => ({ value: s.id, label: s.name })),
              ]}
            />
            <input
              className="input"
              value={roleInTeam}
              onChange={(e) => setRoleInTeam(e.target.value)}
              maxLength={200}
              placeholder={t("complaints.team.rolePlaceholder", "Zadatak u timu")}
              style={{ minWidth: 200 }}
            />
            <button type="button" className="btn primary sm" disabled={busy || !userId} onClick={() => void add()}>
              <Ic.plus /> {t("complaints.team.add", "Dodaj")}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

/** Error codes the complaint team path can return, in the reader's language. */
function errorText(t: Translate, err: ApiError): string {
  switch (err.code) {
    case "NOT_INTERNAL_USER":
      return t("complaints.error.notInternalUser", "Na reklamaciji rade samo zaposleni Bankoma.");
    case "TEAM_MEMBER_ALREADY_ON":
      return t("complaints.error.teamMemberAlreadyOn", "Ta osoba je već u timu.");
    case "USER_NOT_FOUND":
      return t("complaints.error.userNotFound", "Taj nalog više ne postoji.");
    default:
      return err.message;
  }
}
